import { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Tabs, Tab, Typography, TextField, Grid, CircularProgress, Snackbar, Alert,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { Print, Close, Edit, Visibility, Save } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import { ipdAPI } from '../services/api';

/* ── helpers ─────────────────────────────────────────── */
const calcAge = (dob) => {
  if (!dob) return '';
  const b = new Date(dob), t = new Date();
  let age = t.getFullYear() - b.getFullYear();
  if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) age--;
  return age;
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });

/* ── shared hospital header ──────────────────────────── */
const HospitalHeader = () => (
  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
    <tbody>
      <tr>
        <td style={{ width: 88, verticalAlign: 'middle', padding: '4px 6px' }}>
          <img src="/logo.png" alt="MSG Logo" style={{ width: 76, height: 76, objectFit: 'contain' }} />
        </td>
        <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '4px 0' }}>
          <div style={{ fontSize: 17, fontWeight: 900, color: '#1b5e20' }}>MSG NATUROPATHY CENTER</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#2e7d32', marginTop: 2 }}>(MANAGED BY ARZ UNIQUE ENTERPRISES LIMITED)</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#1b5e20', marginTop: 2 }}>Sirsa, Haryana</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#1b5e20', marginTop: 2 }}>PHONE- +91 7082999551 / +91 7082999351</div>
        </td>
        <td style={{ width: 88, verticalAlign: 'middle', padding: '4px 6px' }}></td>
      </tr>
    </tbody>
  </table>
);

/* ── fill line ───────────────────────────────────────── */
const FL = ({ value = '', minWidth = 180, bold = false }) => (
  <span style={{
    borderBottom: '1px solid #333', display: 'inline-block', minWidth,
    paddingBottom: 1, fontWeight: bold ? 700 : 400, lineHeight: 1.4, verticalAlign: 'bottom'
  }}>{value || '\u00A0'}</span>
);

/* ── blank writing area (shows text if filled) ───────── */
const WriteArea = ({ lines = 4, value = '' }) => {
  if (value && value.trim()) {
    return (
      <div style={{
        margin: '4px 0', border: '1px solid #aaa', borderRadius: 2,
        padding: '4px 6px', whiteSpace: 'pre-wrap', minHeight: lines * 22, fontSize: 12
      }}>
        {value}
      </div>
    );
  }
  return (
    <div style={{ margin: '4px 0' }}>
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} style={{ borderBottom: '1px solid #555', margin: '9px 0', minHeight: 14 }} />
      ))}
    </div>
  );
};

/* ── bold section box ────────────────────────────────── */
const SectionBox = ({ children }) => (
  <div style={{
    border: '1px solid #333', textAlign: 'center', fontWeight: 700,
    fontSize: 12, padding: '4px 6px', margin: '12px 0 6px 0', background: '#f0f0f0'
  }}>{children}</div>
);

const HR = () => <hr style={{ border: '1px solid #333', margin: '6px 0' }} />;

/* ── static field definitions for mapped sections ────── */
// Each entry: [leftLabel, leftKey, rightLabel, rightKey]
const PERSONAL_PAIRS = [
  ['(A) Food Type',    'foodType',      'Appetite',            'appetite'],
  ['(B) Allergy',      'allergies',     'Habit / Addiction',   'habit'],
  ['(C) Micturation',  'micturation',   'Bowel habits',        'bowelHabits'],
  ['Marital status',   'maritalStatus', 'Sexual weakness',     'sexualWeakness'],
  ['Mode of work',     'modeOfWork',    'Total working hours', 'totalWorkingHours'],
  ['Exercise / Yoga',  'exercise',      'Sleeping type',       'sleepingType'],
];

// Each entry: [leftLabel, leftKey, rightLabel, rightKey]
const PHYS_PAIRS = [
  ['Pallour :-',       'pallour',      'Icterus :-',            'icterus'],
  ['Cyanosis',         'cyanosis',     'Oedema :-',             'oedema'],
  ['Clubbing :-',      'clubbing',     'Lymphadinopathy :-',    'lymphadinopathy'],
  ['Eye :-',           'eye',          'Ear',                   'ear'],
  ['Lips :-',          'lips',         'Nostril',               'nostril'],
  ['Hair :-',          'hair',         'Head :-',               'head'],
  ['Throat / Neck :-', 'throatNeck',   'Teeth / Gums :-',       'teethGums'],
  ['Mouth / face :-',  'mouthFace',    'Genitals :-',           'genitals'],
];

const SYSTEMIC = [
  ['Cardio Vascular System', 'cardioVascular'],
  ['Central Nervous System', 'cns'],
  ['Respiratory System', 'respiratory'],
  ['Renal System', 'renal'],
  ['Locomotor System', 'locomotor'],
  ['Reproductive System', 'reproductive'],
  ['Per Abdominal Examination', 'perAbdominal'],
  ['Integumentary System', 'integumentary'],
  ['Psychiatary And Psychological System', 'psychiatry'],
];

/* ══════════════════════════════════════════════════════
   FORM 1 — Permission for Admission for IPD
══════════════════════════════════════════════════════ */
export const PermissionForm = ({ patient, admission, overrides = {} }) => {
  const fullName = patient
    ? `${patient.firstName || patient.first_name || ''} ${patient.lastName || patient.last_name || ''}`.trim()
    : '';
  const dob = patient?.dateOfBirth || patient?.date_of_birth || '';
  const dobStr = dob
    ? new Date(dob).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '/')
    : '';
  const age = calcAge(dob);

  return (
    <div style={{ fontFamily: 'Times New Roman, serif', fontSize: 12, color: '#111', lineHeight: 1.5 }}>
      <div style={{ border: '2px solid #333', padding: '10px 14px' }}>
        <HospitalHeader />
        <HR />
        <SectionBox>
          PERSONAL DETAIL<br />
          <span style={{ fontSize: 11 }}>व्यक्तिगत विवरण</span>
        </SectionBox>

        <div style={{ lineHeight: 2.2, marginTop: 12, fontSize: 12 }}>
          <div style={{ fontWeight: 700 }}>It is advices to admitted the below mentioned health seeker</div>
          <div style={{ fontSize: 13, fontWeight: 700, margin: '2px 0 10px 0' }}>
            (निम्न स्वास्थ्य साधक को अंतरंग चिकित्सय विभाग में भर्ती किया जाए)
          </div>

          <div><strong>Health Seeker Name (स्वास्थ्य साधक का नाम) :</strong>&nbsp;<FL value={fullName} minWidth={320} bold /></div>
          <div><strong>Father's/ Husband Name (पिता/पति का नाम) :</strong>&nbsp;<FL value={overrides.fatherName || ''} minWidth={310} /></div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div><strong>Gender (लिंग) :-</strong>&nbsp;<FL value={patient?.gender ? patient.gender.charAt(0).toUpperCase() : ''} minWidth={36} /></div>
            <div><strong>Date of Birth (जन्मतिथि):-</strong>&nbsp;<FL value={dobStr} minWidth={90} /></div>
            <div><strong>Age (उम्र)</strong>&nbsp;<FL value={age ? `${age}` : ''} minWidth={36} /></div>
            <div><strong>Blood Group (ब्लड ग्रुप्स): -</strong>&nbsp;<FL value={overrides.bloodGroup || ''} minWidth={46} /></div>
          </div>
          <div style={{ display: 'flex', gap: 24 }}>
            <div style={{ flex: 1 }}><strong>Mobile No</strong>&nbsp;<FL value={patient?.phone || ''} minWidth={160} /></div>
            <div style={{ flex: 1 }}>Alt. mobile no: -&nbsp;<FL value={overrides.altMobile || ''} minWidth={160} /></div>
          </div>
          <div style={{ display: 'flex', gap: 24 }}>
            <div><strong>ID Proof :-</strong>&nbsp;<FL value={overrides.idProof || ''} minWidth={120} /></div>
            <div>ID Proof details :-&nbsp;<FL value={overrides.idProofDetails || ''} minWidth={240} /></div>
          </div>
          <div><strong>Address (पता)</strong>&nbsp;<FL value={patient?.address || ''} minWidth={420} /></div>
          <div><FL value="" minWidth={510} /></div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div>City: -&nbsp;<FL value={overrides.city || ''} minWidth={100} /></div>
            <div>State: -&nbsp;<FL value={overrides.state || ''} minWidth={100} /></div>
            <div>Country: -&nbsp;<FL value={overrides.country || ''} minWidth={80} /></div>
            <div>Pin Code: -&nbsp;<FL value={overrides.pinCode || ''} minWidth={72} /></div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 28 }}>
          <div style={{ fontSize: 12 }}><strong>DATE –</strong>&nbsp;<FL value={fmtDate(admission?.admissionDate) || today} minWidth={150} /></div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: 30 }}>&nbsp;</div>
            <div style={{ borderTop: '1px solid #333', paddingTop: 4, fontSize: 12, minWidth: 160 }}>
              <strong>SIGNATURE</strong><br />हस्ताक्षर
            </div>
          </div>
        </div>

        <div style={{ marginTop: 20, lineHeight: 2.0, fontSize: 12 }}>
          <div>
            <strong>The Above Mentioned Patient Is Admitted In I.P.D Bed/Room No</strong>&nbsp;
            <FL value={admission?.roomNumber || ''} minWidth={110} />
            &nbsp;<strong>On Date</strong>&nbsp;
            <FL value={fmtDate(admission?.admissionDate) || ''} minWidth={110} />
          </div>
          <div style={{ fontSize: 11 }}>
            उपयुक्त रोगी आई.पी.डी. बिस्तर/कमरा संख्या&nbsp;
            <FL value={admission?.roomNumber || ''} minWidth={90} />
            &nbsp;पर दिनांक&nbsp;
            <FL value={fmtDate(admission?.admissionDate) || ''} minWidth={90} />
            &nbsp;भर्ती किया गया
          </div>
          <div style={{ display: 'flex', gap: 24 }}>
            <div>UHID No (UHID नं ):&nbsp;<FL value={patient?.patientCode || patient?.patient_code || ''} minWidth={200} /></div>
            <div>OPD NO (ओपीडी संख्या)&nbsp;<FL value={admission?.visitId ? String(admission.visitId) : ''} minWidth={160} /></div>
          </div>
          <div style={{ display: 'flex', gap: 24 }}>
            <div>Date of admission:-&nbsp;<FL value={fmtDate(admission?.admissionDate) || ''} minWidth={130} /></div>
            <div>Date of discharge :-&nbsp;<FL value={fmtDate(admission?.dischargeDate) || ''} minWidth={130} /></div>
          </div>
        </div>

        <div style={{ marginTop: 24, fontWeight: 700, fontSize: 12 }}>SIGNATURE BOOKING COUNTER/ OFFICE</div>

        <div style={{ marginTop: 16, borderTop: '1px solid #aaa', paddingTop: 6, textAlign: 'center' }}>
          <span style={{ color: '#c00', fontWeight: 700, fontSize: 11 }}>Note-Not For Medico legal Purpose</span>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   FORM 2 — Patient's Case History (2 pages)
══════════════════════════════════════════════════════ */
export const CaseHistoryForm = ({ patient, overrides = {} }) => {
  const fullName = patient
    ? `${patient.firstName || patient.first_name || ''} ${patient.lastName || patient.last_name || ''}`.trim()
    : '';
  const age = calcAge(patient?.dateOfBirth || patient?.date_of_birth || '');
  const gender = patient?.gender ? patient.gender.charAt(0).toUpperCase() : '';

  const Page = ({ children }) => (
    <div style={{ fontFamily: 'Times New Roman, serif', fontSize: 12, color: '#111' }}>
      <div style={{ border: '2px solid #333', padding: '10px 14px' }}>
        <HospitalHeader />
        <HR />
        {children}
      </div>
    </div>
  );

  return (
    <>
      {/* ── Page 1 ── */}
      <Page>
        <SectionBox>PATIENT'S CASE HISTORY (स्वास्थ्य साधक विवरणिका)</SectionBox>
        <div style={{ fontSize: 12, lineHeight: 2.0, marginTop: 4 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div>Patients Name: -&nbsp;<FL value={fullName} minWidth={180} bold /></div>
            <div>UHID:-&nbsp;<FL value={patient?.patientCode || patient?.patient_code || ''} minWidth={100} /></div>
            <div>Gender&nbsp;<FL value={gender} minWidth={30} /></div>
            <div>Age&nbsp;<FL value={age ? `${age}` : ''} minWidth={30} /></div>
          </div>
        </div>

        <SectionBox>MAIN COMPLAINT</SectionBox>
        <div style={{ fontSize: 12 }}>Present Complaint &amp; Duration</div>
        <WriteArea lines={6} value={overrides.presentComplaint || ''} />
        <div style={{ fontSize: 12, marginTop: 6 }}>Past History Regarding Diseases And Duration</div>
        <WriteArea lines={5} value={overrides.pastHistory || ''} />
        <div style={{ fontSize: 12, marginTop: 6 }}>Family History</div>
        <WriteArea lines={4} value={overrides.familyHistory || ''} />

        <SectionBox>PRESENT MEDICATION IN ANY</SectionBox>
        <WriteArea lines={5} value={overrides.currentMedications || ''} />
      </Page>

      <div style={{ pageBreakBefore: 'always', breakBefore: 'page' }} />

      {/* ── Page 2 ── */}
      <Page>
        <SectionBox>PERSONAL HISTORY DETAILS</SectionBox>
        <div style={{ fontSize: 12, lineHeight: 2.0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {PERSONAL_PAIRS.map(([lLabel, lKey, rLabel, rKey]) => (
                <tr key={lKey}>
                  <td style={{ width: '50%', paddingRight: 12, paddingBottom: 2 }}>
                    {lLabel}&nbsp;<FL value={overrides[lKey] || ''} minWidth={140} />
                  </td>
                  <td style={{ width: '50%', paddingBottom: 2 }}>
                    {rLabel}:-&nbsp;<FL value={overrides[rKey] || ''} minWidth={140} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <SectionBox>GENERAL PHYSICAL EXAMINATATION</SectionBox>
        <div style={{ fontSize: 12, lineHeight: 2.0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {PHYS_PAIRS.map(([lLabel, lKey, rLabel, rKey]) => (
                <tr key={lKey}>
                  <td style={{ width: '50%', paddingRight: 12, paddingBottom: 2 }}>
                    {lLabel}&nbsp;<FL value={overrides[lKey] || ''} minWidth={140} />
                  </td>
                  <td style={{ width: '50%', paddingBottom: 2 }}>
                    {rLabel}&nbsp;<FL value={overrides[rKey] || ''} minWidth={140} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <SectionBox>SYSTEMIC EXAMINATATION</SectionBox>
        <div style={{ fontSize: 12, lineHeight: 2.2 }}>
          {SYSTEMIC.map(([label, key]) => (
            <div key={key}>{label}&nbsp;<FL value={overrides[key] || ''} minWidth={310} /></div>
          ))}
        </div>

        <SectionBox>DIAGNOSIS</SectionBox>
        <WriteArea lines={4} value={overrides.caseHistoryDiagnosis || ''} />
      </Page>
    </>
  );
};

/* ══════════════════════════════════════════════════════
   FORM 3 — Informed Consent Form (pages 1-2)
   FORM 4 — Treatment Consent Form (pages 3-4)
══════════════════════════════════════════════════════ */
export const ConsentForm = ({ patient, overrides = {} }) => {
  const fullName = patient
    ? `${patient.firstName || patient.first_name || ''} ${patient.lastName || patient.last_name || ''}`.trim()
    : '';

  const CONSENT_POINTS = [
    {
      en: 'The management reserves the right of admission or discharge of any patient without any reason whatsoever.',
      hi: 'रोगी को भर्ती तथा डिस्चार्ज करने का अधिकार प्रबंधन के पास सुरक्षित है।'
    },
    {
      en: 'I confirm that the proposed treatment/procedure has been explained to me in terms and in the language that I understand.',
      hi: 'मैं अपनी समझ में आने वाले शब्दों और भाषा में निम्नलिखित स्वीकार कर रहा हूँ।'
    },
    {
      en: 'I have been explained and provided requisite information. I have understood the nature, purpose, and scope of the proposed treatment/procedure, and I consent, authorize and direct the doctor-in-charge and his/her team to perform the proposed procedure.',
      hi: 'मुझे समझाया गया है तथा अपेक्षित जानकारी प्रदान की गई है।'
    },
    {
      en: 'I have been explained and understood that unforeseen circumstances may arise during the course of treatment which may necessitate performing additional or different procedures. I consent to such modifications as determined by the doctor-in-charge.',
      hi: 'मुझे समझाया गया है कि प्रस्तावित उपचार के दौरान अप्रत्याशित परिस्थितियों के कारण कुछ अधिक या अलग करना पड़ सकता है।'
    },
    {
      en: 'I have been explained and understood the alternative methods and therapies of the proposed treatment/procedure, including their respective benefits, material risks and disadvantages.',
      hi: 'मुझे वैकल्पिक तरीकों, उनके संबंधित लाभों, भौतिक जोखिम और नुकसान के बारे में बताया गया है।'
    },
    {
      en: 'I acknowledge the potential risks and benefits of the treatment and voluntarily choose to proceed. In the event of any complication, the management reserves the right to refer me to any appropriate medical institution. I understand that the centre/management shall not be held liable for any casualty, complication, or adverse reaction that may occur.',
      hi: 'मैं उपचार के संभावित लाभ और जोखिमों को स्वीकार करता/करती हूँ और स्वेच्छा से इस उपचार को कराने के लिए सहमत हूँ।'
    },
    {
      en: 'I confirm that I have been provided an opportunity to ask questions regarding the procedure, and the doctor-in-charge has answered all my questions to my satisfaction.',
      hi: 'प्रभारी चिकित्सक ने प्रस्तावित उपचार के संबंध में मेरे सभी प्रश्नों के संतोषजनक उत्तर दिए हैं।'
    },
    {
      en: 'I acknowledge that, despite the best efforts of the medical team, there can be no assurance about the result of proposed treatment/procedure. No guarantees or warranties have been made to me concerning the results.',
      hi: 'मुझे समझाया गया है कि सर्वोत्तम प्रयासों के बावजूद प्रस्तावित उपचार के परिणाम के बारे में कोई आश्वासन नहीं दिया जा सकता।'
    },
    {
      en: 'I have been explained and understood that despite all precautions, complications may occur that may even result in death or serious disability.',
      hi: 'सभी सावधानियों के बावजूद, जटिलताएं हो सकती हैं जिसके परिणाम स्वरूप गंभीर विकलांगता एवं मृत्यु भी हो सकती है।'
    },
    {
      en: 'I have been advised of my option to take a second opinion from another doctor regarding the proposed treatment/procedure.',
      hi: 'मुझे प्रस्तावित उपचार के संबंध में किसी अन्य चिकित्सक से दूसरी राय लेने की सलाह दी गई है।'
    },
    {
      en: 'I state and confirm that following a thorough explanation, counselling and necessary disclosures, I have been given sufficient time for deliberation and to make an informed decision before providing my consent.',
      hi: 'समझाने, परामर्श और खुलासे के बाद मुझे सहमति देने के लिए पर्याप्त समय दिया गया था।'
    },
    {
      en: 'I declare that I am signing this consent form voluntarily, out of my own free will, and without any kind of pressure or coercion or undue influence.',
      hi: 'मैंने इस सहमति पत्र पर अपनी स्वतंत्र इच्छा से और बिना किसी प्रकार के दबाव के स्वेच्छा से हस्ताक्षर किए हैं।'
    },
  ];

  const Page = ({ children }) => (
    <div style={{ fontFamily: 'Times New Roman, serif', fontSize: 12, color: '#111' }}>
      <div style={{ border: '2px solid #333', padding: '10px 14px' }}>
        <HospitalHeader />
        <HR />
        {children}
      </div>
    </div>
  );

  return (
    <>
      {/* Informed Consent Form */}
      <Page>
        <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 14, textDecoration: 'underline', margin: '8px 0' }}>
          Informed Consent Form
        </div>
        <div style={{ lineHeight: 1.75, fontSize: 12 }}>
          <p>
            This Informed Consent Form <strong>("Consent Form")</strong> constitutes a binding agreement between MSG Naturopathy
            wellness and the undersigned&nbsp;<FL value={fullName} minWidth={220} bold />
          </p>
          <p>Heath Seeker shall review the points before filling out this Consent Form</p>
          <p>Relationship with the patient, if any (रोगी के साथ संबंध, यदि कोई हो)&nbsp;<FL value={overrides.relationship || ''} minWidth={165} /></p>
          <p>
            <strong>I, the undersigned, do hereby voluntarily, knowingly, and without any pressure or coercion, provide my
            consent. State and confirm as follows:</strong>
          </p>
          <p style={{ margin: '4px 0 10px 0' }}>
            अधो हस्ताक्षरी एतद्द्वारा निम्नानुसार बताते हैं और पुष्टि करते हैं:
          </p>
          {CONSENT_POINTS.map((pt, i) => (
            <div key={i} style={{ marginBottom: 9 }}>
              <div><strong>{i + 1}.</strong>&nbsp;{pt.en}</div>
              <div style={{ fontSize: 11, color: '#222', marginTop: 2, paddingLeft: 16 }}>{pt.hi}</div>
            </div>
          ))}
        </div>
      </Page>

      <div style={{ pageBreakBefore: 'always', breakBefore: 'page' }} />

      {/* Treatment Consent Form */}
      <Page>
        <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 14, textDecoration: 'underline', margin: '8px 0' }}>
          TREATMENT CONSENT FORM
        </div>
        <div style={{ lineHeight: 1.75, fontSize: 12 }}>
          <p>
            I,&nbsp;<FL value={fullName} minWidth={220} bold />, hereby give my informed consent to undergo Panchakarma and
            Naturopathy treatment, a traditional Ayurvedic detoxification and rejuvenation therapy. I understand that this
            treatment is designed to cleanse my body, balance my doshas (Vata, Pitta, Kapha), and promote overall well-being.
          </p>
          <p>
            <strong>The treatment may include the following procedures:</strong><br />
            Naturopathy Treatments – Dietary Modifications, Fasting, Hydrotherapy, Mud Therapy, Yoga and Pranayama,
            Massage Therapy, Colon Hydrotherapy, Acupuncture, Ozone Therapy.<br />
            Panchakarma Procedures – Nasya (Nasal Therapy), Abhyanga (Oil Massage), Swedana (Steam Therapy), Other
            Ayurveda therapies as recommended.
          </p>

          <div><strong>Expected Benefits – अपेक्षित लाभ</strong></div>
          {[
            'Detoxification and cleansing of the body – शरीर का डिटॉक्सीफिकेशन और शुद्धिकरण',
            'Rejuvenation of the body and mind – शरीर और मन का पुनरुत्थान (पुनर्जीवन)',
            'Improved digestion and metabolism – पाचन और चयापचय में सुधार',
            'Increased vitality and energy – जीवनशक्ति और ऊर्जा में वृद्धि',
            'Stress relief and mental clarity – तनाव में कमी और मानसिक स्पष्टता',
            'Strengthened immune system – रोग-प्रतिरोधक क्षमता में वृद्धि',
          ].map((b, i) => <div key={i}>• {b}</div>)}

          <div style={{ marginTop: 8 }}><strong>Potential Risks &amp; Side Effects – संभावित जोखिम और दुष्प्रभाव</strong></div>
          {[
            'Mild nausea or dizziness – हल्की मतली या चक्कर आना',
            'Fatigue or weakness – थकावट या कमजोरी',
            'Headaches – सिरदर्द',
            'Skin irritation (due to treatments like oil massage) – त्वचा में जलन',
            'Temporary changes in digestion – पाचन में अस्थायी परिवर्तन',
            'Emotional release or mood changes – भावनात्मक प्रतिक्रिया या मनोदशा में परिवर्तन',
            'Possible discomfort during certain therapies like fasting or steam treatments',
          ].map((r, i) => <div key={i}>• {r}</div>)}

          <div style={{ marginTop: 8 }}><strong>Final Declaration (अंतिम घोषणा):</strong></div>
          {[
            'I have been informed of these risks and understand that any discomfort or side effects will be communicated to the practitioner during the treatment & I have provided complete information about my medical history, including any allergies, medications, or pre-existing conditions.',
            'I confirm that I do not have the following conditions: Pregnancy, Severe heart disease or cardiovascular issues, Active infection, Psychiatric conditions that are unstable, Any other condition deemed unsuitable by the Ayurvedic practitioner.',
            'If I have any of the above conditions or other concerns, I will inform the practitioner before beginning the treatment.',
            'I understand that the success of the treatment will depend on my cooperation with the guidelines provided.',
            'I confirm that I have been provided with sufficient information about the Panchakarma and naturopathy treatment process. I understand the nature of the therapies, the expected benefits, and the potential risks. I voluntarily consent to undergoing Panchakarma and naturopathy treatment and acknowledge that I have the right to withdraw my consent at any time.',
            'I understand that this is a holistic treatment, and individual results may vary.',
            'By signing this form, I acknowledge that I have read and understood all the information provided, and I give my consent to undergo the Panchakarma and naturopathy treatments.',
          ].map((d, i) => <div key={i} style={{ marginTop: 5 }}>• {d}</div>)}

          <div style={{ marginTop: 24 }}>
            Patient Signature:-&nbsp;<FL value="" minWidth={255} />
          </div>
          <div style={{ marginTop: 16 }}>
            Date:&nbsp;<FL value={today} minWidth={148} />
          </div>
        </div>
      </Page>
    </>
  );
};

/* ── Module-level sub-components (must be outside EditPanel to avoid remount on each keystroke) */
const FormField = ({ label, field, multiline, rows = 1, xs = 12, overrides, onChange }) => (
  <Grid item xs={xs}>
    <TextField
      label={label}
      value={overrides[field] || ''}
      onChange={(e) => onChange({ ...overrides, [field]: e.target.value })}
      fullWidth
      size="small"
      multiline={multiline}
      rows={multiline ? rows : undefined}
    />
  </Grid>
);

const EditSection = ({ title }) => (
  <Grid item xs={12}>
    <Typography
      variant="subtitle2"
      sx={{ mt: 2, mb: 0.5, fontWeight: 700, color: 'primary.main',
            borderBottom: '2px solid', borderColor: 'primary.light', pb: 0.5 }}
    >
      {title}
    </Typography>
  </Grid>
);

/* ══════════════════════════════════════════════════════
   EDIT PANEL — fills in the blank fields before printing
══════════════════════════════════════════════════════ */
function EditPanel({ overrides, onChange }) {
  // f() is a plain function call (not a JSX component) so React never unmounts/remounts
  // the underlying FormField on re-render — focus is preserved between keystrokes.
  const f = (label, field, xs = 12, multiline = false, rows = 1) => (
    <FormField
      key={field}
      label={label}
      field={field}
      xs={xs}
      multiline={multiline}
      rows={rows}
      overrides={overrides}
      onChange={onChange}
    />
  );
  const S = (title) => <EditSection key={title} title={title} />;

  return (
    <Box sx={{ p: 1.5 }}>
      <Grid container spacing={1.5}>

        {S('Permission Form — Personal Details')}
        {f("Father's / Husband Name", 'fatherName', 6)}
        {f('Blood Group', 'bloodGroup', 3)}
        {f('Alt. Mobile No', 'altMobile', 3)}
        {f('ID Proof Type (e.g. Aadhar, PAN)', 'idProof', 6)}
        {f('ID Proof Number / Details', 'idProofDetails', 6)}
        {f('City', 'city', 3)}
        {f('State', 'state', 3)}
        {f('Country', 'country', 3)}
        {f('Pin Code', 'pinCode', 3)}

        {S('Main Complaint')}
        {f('Present Complaint & Duration', 'presentComplaint', 12, true, 3)}
        {f('Past History Regarding Diseases and Duration', 'pastHistory', 12, true, 3)}
        {f('Family History', 'familyHistory', 12, true, 2)}
        {f('Present Medication (if any)', 'currentMedications', 12, true, 3)}
        {f('Diagnosis', 'caseHistoryDiagnosis', 12, true, 2)}

        {S('Personal History')}
        {f('Food Type', 'foodType', 4)}
        {f('Appetite', 'appetite', 4)}
        {f('Allergy', 'allergies', 4)}
        {f('Habit / Addiction', 'habit', 4)}
        {f('Micturation', 'micturation', 4)}
        {f('Bowel Habits', 'bowelHabits', 4)}
        {f('Marital Status', 'maritalStatus', 4)}
        {f('Sexual Weakness', 'sexualWeakness', 4)}
        {f('Mode of Work', 'modeOfWork', 4)}
        {f('Total Working Hours', 'totalWorkingHours', 4)}
        {f('Exercise / Yoga', 'exercise', 4)}
        {f('Sleeping Type', 'sleepingType', 4)}

        {S('Physical Examination')}
        {[
          ['Pallour', 'pallour'], ['Icterus', 'icterus'],
          ['Cyanosis', 'cyanosis'], ['Oedema', 'oedema'],
          ['Clubbing', 'clubbing'], ['Lymphadinopathy', 'lymphadinopathy'],
          ['Eye', 'eye'], ['Ear', 'ear'],
          ['Lips', 'lips'], ['Nostril', 'nostril'],
          ['Hair', 'hair'], ['Head', 'head'],
          ['Throat / Neck', 'throatNeck'], ['Teeth / Gums', 'teethGums'],
          ['Mouth / Face', 'mouthFace'], ['Genitals', 'genitals'],
        ].map(([label, field]) => f(label, field, 4))}

        {S('Systemic Examination')}
        {SYSTEMIC.map(([label, key]) => f(label, key, 6))}

        {S('Consent Form')}
        {f('Relationship with Patient (if any)', 'relationship', 6)}

      </Grid>
    </Box>
  );
}

const parseAddress = (addressStr) => {
  if (!addressStr) return { city: '', state: '', country: 'India', pinCode: '' };
  const pinMatch = addressStr.match(/\b\d{6}\b/);
  const pinCode = pinMatch ? pinMatch[0] : '';
  const parts = addressStr.split(',').map(p => p.trim());
  let country = 'India';
  if (addressStr.toLowerCase().includes('india')) {
    country = 'India';
  }
  const states = ['Haryana', 'Punjab', 'Rajasthan', 'Delhi', 'Uttar Pradesh', 'UP', 'HP', 'Himachal Pradesh', 'Gujarat'];
  let state = '';
  for (const s of states) {
    if (addressStr.toLowerCase().includes(s.toLowerCase())) {
      state = s;
      break;
    }
  }
  let city = '';
  if (parts.length > 1) {
    if (state) {
      const stateIdx = parts.findIndex(p => p.toLowerCase().includes(state.toLowerCase()));
      if (stateIdx > 0) {
        city = parts[stateIdx - 1];
      }
    } else {
      city = parts[parts.length - 2] || parts[0];
    }
  } else {
    city = parts[0] || '';
  }
  return { city, state, country, pinCode };
};

/* ── initial overrides — seeded from admission clinical notes where available */
const buildOverrides = (admission, patient) => {
  const parsed = parseAddress(patient?.address || '');
  return {
    // Permission form — personal details
    fatherName: patient?.fatherHusbandName || patient?.father_husband_name || '',
    bloodGroup: '', altMobile: '', idProof: '', idProofDetails: '',
    city: parsed.city,
    state: parsed.state,
    country: parsed.country,
    pinCode: parsed.pinCode,
  // Main complaint (page 1)
  presentComplaint: admission?.chiefComplaint || admission?.chief_complaint || patient?.chiefComplaint || patient?.chief_complaint || '',
  pastHistory:      admission?.pastMedicalHistory || admission?.past_medical_history || patient?.pastMedicalHistory || patient?.past_medical_history || '',
  familyHistory:    admission?.familyHistory || admission?.family_history || patient?.familyHistory || patient?.family_history || '',
  currentMedications: admission?.currentMedications || admission?.current_medications || patient?.currentMedications || patient?.current_medications || '',
  caseHistoryDiagnosis: admission?.diagnosis || patient?.diagnosis || '',
  // Personal history (page 2)
  foodType: '', appetite: '',
  allergies: '',
  habit: '', micturation: '', bowelHabits: '',
  maritalStatus: '', sexualWeakness: '',
  modeOfWork: '', totalWorkingHours: '',
  exercise: '', sleepingType: '',
  // Physical examination
  pallour: '', icterus: '', cyanosis: '', oedema: '',
  clubbing: '', lymphadinopathy: '',
  eye: '', ear: '', lips: '', nostril: '',
  hair: '', head: '', throatNeck: '', teethGums: '',
  mouthFace: '', genitals: '',
  // Systemic examination
  cardioVascular: '', cns: '', respiratory: '', renal: '', locomotor: '',
  reproductive: '', perAbdominal: '', integumentary: '', psychiatry: '',
  // Consent form
  relationship: '',
  };
};

/* ══════════════════════════════════════════════════════
   MAIN DIALOG
══════════════════════════════════════════════════════ */
function IPDAdmissionForms({ patient, admission, onClose }) {
  const [tab, setTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [overrides, setOverrides] = useState(() => buildOverrides(admission, patient));
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const printRef = useRef();

  const [printSelection, setPrintSelection] = useState('all');
  const admissionId = admission?.admissionId || admission?.id || admission?.admission_id;

  // Load persisted overrides on open (merge saved values over defaults)
  useEffect(() => {
    if (!admissionId) return;
    ipdAPI.getFormOverrides(admissionId)
      .then(res => {
        const saved = res.data.data;
        if (saved && typeof saved === 'object' && Object.keys(saved).length > 0) {
          setOverrides(prev => ({ ...prev, ...saved }));
        }
      })
      .catch(() => {}); // silently ignore if column not yet migrated
  }, [admissionId]);

  const handleSave = async () => {
    if (!admissionId) return;
    setSaving(true);
    try {
      await Promise.all([
        // Save clinical notes (syncs to ipd_admissions + patients)
        ipdAPI.updateClinicalNotes(admissionId, {
          chiefComplaint:     overrides.presentComplaint      || null,
          diagnosis:          overrides.caseHistoryDiagnosis  || null,
          pastMedicalHistory: overrides.pastHistory           || null,
          currentMedications: overrides.currentMedications    || null,
          familyHistory:      overrides.familyHistory         || null,
          doctorNotes:        admission?.doctorNotes || admission?.doctor_notes || null,
        }),
        // Save full overrides blob (all other fields: father name, blood group, etc.)
        ipdAPI.saveFormOverrides(admissionId, overrides),
      ]);
      setSaveSuccess('Details saved successfully');
    } catch (err) {
      setSaveSuccess('Failed to save — please try again');
    } finally {
      setSaving(false);
    }
  };

  const doPrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `IPD_Forms_${patient?.patientCode || patient?.patient_code || 'Patient'}`
  });

  const handlePrint = async () => {
    if (admissionId) await handleSave();
    doPrint();
  };

  const patientLabel = patient
    ? `${patient.firstName || patient.first_name || ''} ${patient.lastName || patient.last_name || ''}`.trim()
    : '';

  return (
    <>
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { maxHeight: '95vh' } }}>
      <DialogTitle>
        IPD Admission Forms{patientLabel ? ` — ${patientLabel}` : ''}
      </DialogTitle>

      {!editMode && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label="Permission for Admission" />
            <Tab label="Patient Case History" />
            <Tab label="Consent Forms" />
          </Tabs>
        </Box>
      )}

      <DialogContent sx={{ p: 2, bgcolor: editMode ? '#fafafa' : '#e8e8e8', overflowY: 'auto' }}>
        {editMode ? (
          <EditPanel overrides={overrides} onChange={setOverrides} />
        ) : (
          <>
            <Box sx={{ bgcolor: 'white', p: 2, boxShadow: 2, display: tab === 0 ? 'block' : 'none' }}>
              <PermissionForm patient={patient} admission={admission} overrides={overrides} />
            </Box>
            <Box sx={{ bgcolor: 'white', p: 2, boxShadow: 2, display: tab === 1 ? 'block' : 'none' }}>
              <CaseHistoryForm patient={patient} overrides={overrides} />
            </Box>
            <Box sx={{ bgcolor: 'white', p: 2, boxShadow: 2, display: tab === 2 ? 'block' : 'none' }}>
              <ConsentForm patient={patient} overrides={overrides} />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
          {editMode
            ? 'Fill in the blank fields, then Save or switch to Preview before printing.'
            : 'Select form to print or print all forms.'}
        </Typography>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="print-form-select-label">Form to Print</InputLabel>
          <Select
            labelId="print-form-select-label"
            value={printSelection}
            onChange={(e) => setPrintSelection(e.target.value)}
            label="Form to Print"
          >
            <MenuItem value="all">All Forms (5 Pages)</MenuItem>
            <MenuItem value="permission">Permission for Admission</MenuItem>
            <MenuItem value="case_history">Patient Case History</MenuItem>
            <MenuItem value="consent">Consent Forms</MenuItem>
            <MenuItem value="active">Active Tab Form</MenuItem>
          </Select>
        </FormControl>

        <Button
          onClick={() => setEditMode(m => !m)}
          startIcon={editMode ? <Visibility /> : <Edit />}
          variant="outlined"
          color={editMode ? 'success' : 'secondary'}
        >
          {editMode ? 'Preview' : 'Edit Fields'}
        </Button>
        {admissionId && (
          <Button
            onClick={handleSave}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            variant="contained"
            color="success"
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
        <Button onClick={onClose} startIcon={<Close />} variant="outlined">Close</Button>
        <Button onClick={handlePrint} startIcon={<Print />} variant="contained" color="primary" disabled={saving}>
          {printSelection === 'all' ? 'Print All Forms' :
           printSelection === 'permission' ? 'Print Permission Form' :
           printSelection === 'case_history' ? 'Print Case History' :
           printSelection === 'consent' ? 'Print Consent Forms' :
           'Print Selected Form'}
        </Button>
      </DialogActions>
    </Dialog>

    <Snackbar
      open={!!saveSuccess}
      autoHideDuration={3000}
      onClose={() => setSaveSuccess('')}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert severity={saveSuccess.includes('Failed') ? 'error' : 'success'} onClose={() => setSaveSuccess('')}>
        {saveSuccess}
      </Alert>
    </Snackbar>

    {/* Print content rendered directly in document.body via portal so it is outside the
        Dialog's stacking context and react-to-print can measure it properly */}
    {createPortal(
      <div style={{ position: 'fixed', top: 0, left: '-9999px', width: '210mm' }}>
        <div ref={printRef} style={{ background: 'white', width: '210mm' }}>
          <style>{`
            @media print {
              @page { size: A4; margin: 10mm 12mm; }
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          `}</style>
          
          {(printSelection === 'all' || printSelection === 'permission' || (printSelection === 'active' && tab === 0)) && (
            <PermissionForm patient={patient} admission={admission} overrides={overrides} />
          )}

          {printSelection === 'all' && (
            <div style={{ pageBreakBefore: 'always', breakBefore: 'page' }} />
          )}

          {(printSelection === 'all' || printSelection === 'case_history' || (printSelection === 'active' && tab === 1)) && (
            <CaseHistoryForm patient={patient} overrides={overrides} />
          )}

          {printSelection === 'all' && (
            <div style={{ pageBreakBefore: 'always', breakBefore: 'page' }} />
          )}

          {(printSelection === 'all' || printSelection === 'consent' || (printSelection === 'active' && tab === 2)) && (
            <ConsentForm patient={patient} overrides={overrides} />
          )}
        </div>
      </div>,
      document.body
    )}
    </>
  );
}

export default IPDAdmissionForms;
